import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, useApi } from "../lib/api";
import { Badge, Button, Card, ErrorNote, Field, fieldLabel, inputCls, Loading, Table, Td, useAction } from "../components/ui";

export default function AccountDetail() {
  const { id } = useParams();
  const { data, error, loading, reload } = useApi<any>(`/accounts/${id}`, [id]);
  const [sig, setSig] = useState({ type: "job_posting", jobTitle: "", postedDate: "", agency: "", scope: "", dueDate: "", link: "" });
  const act = useAction();
  if (loading && !data) return <Loading />;
  if (!data) return <ErrorNote error={error} />;
  const a = data.account;
  const on = (o: Record<string, boolean>) => Object.entries(o ?? {}).filter(([, v]) => v).map(([k]) => fieldLabel(k));
  const rows: [string, string | number | undefined][] = [
    ["Territory owner", a.territoryOwner],
    ["Tier", a.tier],
    ["Play", a.play],
    ["Fit", on(a.fit).join(", ") || "—"],
    ["Licensed", on(a.licensed).join(", ") || "—"],
    ["Intellias relationship", a.relationship],
    ["Open opps", a.openOpps],
    ["Closed-won", a.closedWon],
  ];
  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-[1.45rem] font-semibold tracking-tight">{a.name}</h1>
        <div className="text-sm text-muted">{[a.industry, a.subIndustry, a.city, a.state, a.domain].filter(Boolean).join(" · ")}</div>
      </div>
      <ErrorNote error={act.error} />
      <div className="grid gap-4 lg:grid-cols-3">
        <Card title="Targeting">
          <dl className="space-y-1 text-sm">
            {rows.map(([k, v]) => (
              <div key={k} className="flex justify-between gap-3"><dt className="text-muted">{k}</dt><dd className="text-right">{v ?? "—"}</dd></div>
            ))}
          </dl>
          <p className="mt-3 text-xs text-muted">Relationship and opportunity fields in the target list are Infor's, not Intellias history. They stay unknown until the Salesforce sync runs.</p>
        </Card>
        <Card title="Public signals" className="lg:col-span-2">
          <ul className="mb-3 space-y-1 text-sm">
            {data.signals.map((s: any) => (
              <li key={s.id} className="flex flex-wrap items-center gap-2">
                <span>{s.type === "rfp" ? `RFP · ${s.agency ?? ""} ${s.scope ?? ""} · due ${s.dueDate ?? "?"}` : `Job posting · ${s.jobTitle} · posted ${s.postedDate ?? "?"}`}</span>
                {s.fresh ? <Badge tone="approved">fresh</Badge> : <Badge tone="skipped">expired</Badge>}
                {s.link && <a className="text-accent-text hover:underline" href={s.link} target="_blank" rel="noreferrer">link</a>}
              </li>
            ))}
            {!data.signals.length && <li className="text-muted">No signals.</li>}
          </ul>
          <div className="grid gap-2 sm:grid-cols-4">
            <Field label="Type"><select className={inputCls} value={sig.type} onChange={(e) => setSig({ ...sig, type: e.target.value })}><option value="job_posting">Job posting</option><option value="rfp">RFP</option></select></Field>
            {sig.type === "rfp" ? (
              <>
                <Field label="Agency"><input className={inputCls} value={sig.agency} onChange={(e) => setSig({ ...sig, agency: e.target.value })} /></Field>
                <Field label="Scope"><input className={inputCls} value={sig.scope} onChange={(e) => setSig({ ...sig, scope: e.target.value })} /></Field>
                <Field label="Due date"><input type="date" className={inputCls} value={sig.dueDate} onChange={(e) => setSig({ ...sig, dueDate: e.target.value })} /></Field>
              </>
            ) : (
              <>
                <Field label="Job title"><input className={inputCls} value={sig.jobTitle} onChange={(e) => setSig({ ...sig, jobTitle: e.target.value })} /></Field>
                <Field label="Posted"><input type="date" className={inputCls} value={sig.postedDate} onChange={(e) => setSig({ ...sig, postedDate: e.target.value })} /></Field>
                <Field label="Link"><input className={inputCls} value={sig.link} onChange={(e) => setSig({ ...sig, link: e.target.value })} /></Field>
              </>
            )}
          </div>
          <Button className="mt-2" onClick={() => act.run(async () => { await api(`/accounts/${id}/signals`, { body: sig }); reload(); })}>Add signal</Button>
        </Card>
      </div>
      <Card title={`Contacts (${data.contacts.length})`}>
        <Table head={["Contact", "Seq.", "State", "Territory owner", "Status", ""]}>
          {data.contacts.map((c: any) => (
            <tr key={c.id}>
              <Td><Link className="font-medium text-accent-text hover:underline" to={`/contacts/${c.id}`}>{c.name}</Link><div className="text-xs text-muted">{c.title}</div></Td>
              <Td>{c.outreachSequence ?? "—"}</Td>
              <Td>{c.state ?? <span className="text-warn">none</span>}</Td>
              <Td>{c.territoryOwner}</Td>
              <Td>{c.status ? <Badge>{c.status}</Badge> : "—"}</Td>
              <Td>{c.noCalls && <Badge tone="Paused">No calls</Badge>}</Td>
            </tr>
          ))}
        </Table>
      </Card>
    </div>
  );
}
